import React, { FormEvent, useMemo, useState } from "react";
import deburr from "lodash/deburr";
import every from "lodash/every";
import kebabCase from "lodash/kebabCase";
import styled from "styled-components";
import { NonIdealState } from "@blueprintjs/core";
import { faReceipt } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Menu, Skeleton, TextInput } from "@mantine/core";

import { AppToaster } from "@components/AppToaster";
import { DEFAULT_POLICY_TEXT } from "@constants";
import { useOrg } from "@hooks/useOrg";
import { useUser } from "@hooks/useUser";
import { Feature } from "@utils/feature";

import { formatPolicyName } from "@pages/finding/utils";
import { InstallPrompt, LoginPrompt } from "@pages/login/components/prompts";
import { savePolicy } from "@pages/policy/api";
import { Policy, PolicyRule } from "@pages/policy/types";
import {
  NOLICY_POLICIES,
  NOLICY_SLUG_TO_APP_COLUMN_NAME,
} from "@pages/ruleBoard/constants";

import "./PolicyMenuStyles.scss";

interface Props {
  policies?: Policy[];
  toAdd: string[];
  onSelect: (slug: string) => Promise<PolicyRule[]>;
}

const LOADING_STATE = (
  <>
    <Menu.Item disabled>
      <Skeleton height={16} />
    </Menu.Item>
    <Menu.Item disabled>
      <Skeleton height={16} />
    </Menu.Item>
    <Menu.Item disabled>
      <Skeleton height={16} />
    </Menu.Item>
  </>
);

const PromptContainer = styled.div`
  padding: 12px;
  width: 300px;
`;

const LOGGED_OUT_STATE = (
  <PromptContainer>
    <LoginPrompt returnPath={window.location.pathname} />
  </PromptContainer>
);
const NO_ORG_STATE = (
  <PromptContainer>
    <InstallPrompt />
  </PromptContainer>
);

// TODO: Use a proper typeahead library/component for this.
export const PolicyMenu: React.FC<Props> = ({ policies, toAdd, onSelect }) => {
  const user = useUser();
  const org = useOrg();
  const [query, setQuery] = useState<string>("");
  const normalizedQuery = useMemo<string>(() => query.toLowerCase(), [query]);
  const isNolicy = Feature.isNolicy({ org });

  const SuccessMessage = (
    toAdd: string[],
    policySlug: string,
    policyName: string,
    notAdded?: number
  ) => {
    const to_link = isNolicy
      ? "/manage/board"
      : "/manage/policies/" + policySlug;
    return (
      <>
        Added {toAdd.length === 1 ? toAdd[0] : toAdd.length + " rules"} to{" "}
        <a href={to_link}>
          {" "}
          <u>{policyName}</u>
          <div>{notAdded ? notAdded + " already in policy" : ""}</div>
        </a>
      </>
    );
  };

  const nolicyPolicyNames = NOLICY_POLICIES.map((p) => p.name);
  const filteredPolicies = useMemo<Policy[] | undefined>(
    () =>
      policies &&
      // if nolicy, sort according to canonical order, otherwise by name
      (isNolicy
        ? policies
            .filter(
              (policy: Policy) =>
                policy.name.toLowerCase().indexOf(normalizedQuery) !== -1
            )
            .sort(
              (a, b) =>
                nolicyPolicyNames.indexOf(a.name) -
                nolicyPolicyNames.indexOf(b.name)
            )
        : policies
            .filter(
              (policy: Policy) =>
                policy.name.toLowerCase().indexOf(normalizedQuery) !== -1
            )
            .sort((a, b) => (a.name > b.name ? 1 : -1))),
    [policies, normalizedQuery, nolicyPolicyNames, isNolicy]
  );
  const haveNewName = useMemo<boolean>(
    () =>
      !normalizedQuery
        ? false
        : every(
            filteredPolicies,
            (policy) => policy.name.toLowerCase() !== normalizedQuery
          ),
    [filteredPolicies, normalizedQuery]
  );

  const createNewPolicy = async () => {
    const newSlug = kebabCase(deburr(normalizedQuery));
    try {
      const newPolicy = await savePolicy(org!.id, {
        name: query,
        slug: newSlug,
        repos: [],
        rules: [],
        is_default: false,
        notifications: [],
        pr_comments: false,
        blocking: false,
        description: DEFAULT_POLICY_TEXT,
        overrides: [],
      });
      await addRulesToPolicy(newPolicy);
    } catch (err) {
      AppToaster.show({
        message: `Failed to create policy: ${err}`,
        intent: "danger",
      });
      return;
    }
  };

  const addRulesToPolicy = async (policy: Policy) => {
    const formatted_policy_name = formatPolicyName(policy.name);
    try {
      const added_rules = await onSelect(policy.slug);
      if (added_rules.length > 0) {
        AppToaster.show({
          timeout: 15000,
          message: SuccessMessage(
            added_rules.map((rule) => rule.id),
            policy.slug,
            formatted_policy_name,
            toAdd.length - added_rules.length
          ),
          intent: "success",
        });
      } else {
        AppToaster.show({
          timeout: 15000,
          message: (
            <>
              {toAdd.length === 1
                ? toAdd[0] + " was"
                : toAdd.length + " rules were"}{" "}
              already in{" "}
              <a href={"/manage/policies/" + policy.slug}>
                {" "}
                <u>{formatted_policy_name}</u>
              </a>{" "}
              (no changes made)
            </>
          ),
          intent: "warning",
        });
      }
    } catch (err) {
      AppToaster.show({
        message: `Failed to add rule to ${formatted_policy_name}: ${err}`,
        intent: "danger",
      });
      return;
    }
  };

  const getPolicyName = (policy: Policy) => {
    if (isNolicy) {
      return NOLICY_SLUG_TO_APP_COLUMN_NAME[policy.slug] + " column";
    }
    return policy.name;
  };

  return user === undefined ? (
    LOGGED_OUT_STATE
  ) : org === undefined ? (
    NO_ORG_STATE
  ) : (
    <>
      {policies && !isNolicy && (
        <TextInput
          placeholder={
            isNolicy ? "Filter policies…" : "Filter policies or create new…"
          }
          onChange={(event: FormEvent<HTMLInputElement>) => {
            setQuery(event.currentTarget.value);
          }}
        />
      )}
      {policies === undefined ? (
        LOADING_STATE
      ) : policies?.length === 0 && !haveNewName ? (
        <NonIdealState
          icon={<FontAwesomeIcon icon={faReceipt} />}
          title="No policies found"
          description="Create your first policy above"
        />
      ) : (
        <>
          {!isNolicy && (
            <Menu.Item
              data-testid={`PolicyMenuMenuItem`}
              className={"createPolicyMenuItem"}
              key={query}
              disabled={
                query === "" ||
                (filteredPolicies &&
                  filteredPolicies.filter(
                    (policy) =>
                      policy.name.toLowerCase() === query.toLowerCase()
                  ).length > 0)
              }
              onClick={createNewPolicy}
            >
              {query === ""
                ? `Create Policy: <enter name above>`
                : `Create Policy: '${query}'`}
            </Menu.Item>
          )}
          {filteredPolicies?.map((policy) => (
            <Menu.Item
              data-testid={`PolicyMenuMenuItem`}
              key={policy.slug}
              onClick={() => addRulesToPolicy(policy)}
            >
              {getPolicyName(policy)}
            </Menu.Item>
          ))}
        </>
      )}
    </>
  );
};
